import { useState } from 'react'
import { useQuery } from 'convex/react'
import { api } from '../../../../../convex/_generated/api'
import { Card } from '@/components/ui/card'
import { ExternalLink } from 'lucide-react'
import { cn } from '@/lib/utils'
import { AttributionChart } from '../AttributionChart'
import {
  ORGANIC_CHANNELS,
  PAID_CHANNELS,
  RANGES,
  STAGE_ORDER,
  STAGE_SHORT,
  colorForChannel,
  labelForChannel,
  formatDate,
  formatNumber,
  formatPct,
} from '../_shared'

const REFERRER_LIMITS = [10, 25, 50, 100] as const

function isLikelyDomain(ref: string): boolean {
  if (!ref || ref === '(direct)') return false
  return /^[a-z0-9.-]+\.[a-z]{2}/i.test(ref)
}

export default function OrganicAttributionPage() {
  const [days, setDays] = useState(30)
  const [showAds, setShowAds] = useState(false)
  const [refLimit, setRefLimit] = useState<(typeof REFERRER_LIMITS)[number]>(25)

  const data = useQuery(api.attribution.getAttributionDashboard, {
    days,
    channels: ORGANIC_CHANNELS,
  })
  const referrers = useQuery(api.attribution.getReferrerBreakdown, {
    days,
    excludeChannels: PAID_CHANNELS,
    limit: refLimit,
  })

  const isLoading = data === undefined
  const refLoading = referrers === undefined

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Organic Attribution</h1>
          <p className="text-sm text-neutral-500">
            Non-paid channels — funnel + top referrers ·{' '}
            {data ? `${formatDate(data.startDate)} – ${formatDate(data.endDate)}` : '—'}
          </p>
        </div>
        <div className="inline-flex rounded-md border border-neutral-200 overflow-hidden">
          {RANGES.map((r) => (
            <button
              key={r.days}
              onClick={() => setDays(r.days)}
              className={cn(
                'px-3 py-1.5 text-xs font-medium transition-colors',
                days === r.days
                  ? 'bg-neutral-900 text-white'
                  : 'bg-white text-neutral-700 hover:bg-neutral-50',
              )}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <AttributionChart
        data={data}
        isLoading={isLoading}
        showAds={showAds}
        onShowAdsChange={setShowAds}
        emptyMessage="No organic traffic in range."
      />

      {/* Top referrers table */}
      <Card className="overflow-hidden p-0">
        <div className="flex items-start justify-between px-4 py-3 border-b border-neutral-200">
          <div>
            <h2 className="text-sm font-semibold text-neutral-900">Top referrers</h2>
            <p className="text-xs text-neutral-500 mt-0.5">
              Where organic visitors are coming from. Stages 4–6 use the user's signup-time
              referrer.
            </p>
          </div>
          <div className="inline-flex rounded-md border border-neutral-200 overflow-hidden">
            {REFERRER_LIMITS.map((n) => (
              <button
                key={n}
                onClick={() => setRefLimit(n)}
                className={cn(
                  'px-2.5 py-1 text-[11px] font-medium transition-colors',
                  refLimit === n
                    ? 'bg-neutral-900 text-white'
                    : 'bg-white text-neutral-700 hover:bg-neutral-50',
                )}
              >
                Top {n}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200 bg-neutral-50/60">
                <th className="text-left px-4 py-2 text-[11px] font-medium text-neutral-500 uppercase tracking-wider">
                  Referrer
                </th>
                <th className="text-left px-4 py-2 text-[11px] font-medium text-neutral-500 uppercase tracking-wider">
                  Channel
                </th>
                {STAGE_ORDER.map((s) => (
                  <th
                    key={s}
                    className="text-right px-4 py-2 text-[11px] font-medium text-neutral-500 uppercase tracking-wider"
                  >
                    {STAGE_SHORT[s]}
                  </th>
                ))}
                <th className="text-right px-4 py-2 text-[11px] font-medium text-neutral-500 uppercase tracking-wider">
                  V→Pro
                </th>
              </tr>
            </thead>
            <tbody>
              {refLoading ? (
                <tr>
                  <td
                    colSpan={STAGE_ORDER.length + 3}
                    className="text-center px-4 py-8 text-neutral-400 text-xs"
                  >
                    Loading…
                  </td>
                </tr>
              ) : referrers!.rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={STAGE_ORDER.length + 3}
                    className="text-center px-4 py-8 text-neutral-400 text-xs"
                  >
                    No referrers in range
                  </td>
                </tr>
              ) : (
                referrers!.rows.map((r) => {
                  const conv = r.visitors > 0 ? (r.proConverted / r.visitors) * 100 : 0
                  const showLink = isLikelyDomain(r.referrer)
                  return (
                    <tr
                      key={r.referrer}
                      className="border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50/60"
                    >
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-medium text-neutral-900 truncate">
                            {r.referrer}
                          </span>
                          {showLink && (
                            <a
                              href={`https://${r.referrer}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-neutral-400 hover:text-neutral-700 flex-shrink-0"
                              title={`Open ${r.referrer}`}
                            >
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="inline-flex items-center gap-1.5">
                          <span
                            className="inline-block h-2 w-2 rounded-full"
                            style={{ background: colorForChannel(r.channel) }}
                          />
                          <span className="text-xs text-neutral-600">
                            {labelForChannel(r.channel)}
                          </span>
                        </div>
                      </td>
                      {STAGE_ORDER.map((s) => (
                        <td
                          key={s}
                          className="px-4 py-2.5 text-right tabular-nums text-neutral-700"
                        >
                          {formatNumber(r[s])}
                        </td>
                      ))}
                      <td className="px-4 py-2.5 text-right tabular-nums">
                        <span
                          className={cn(
                            'font-medium',
                            conv >= 1 ? 'text-emerald-600' : 'text-neutral-400',
                          )}
                        >
                          {formatPct(conv)}
                        </span>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
        {referrers && referrers.total > referrers.rows.length && (
          <div className="px-4 py-2 text-[11px] text-neutral-500 border-t border-neutral-200 bg-neutral-50/60">
            Showing top {referrers.rows.length} of {formatNumber(referrers.total)} referrer sources.
          </div>
        )}
      </Card>

      {/* Funnel by channel */}
      <Card className="overflow-hidden p-0">
        <div className="px-4 py-3 border-b border-neutral-200">
          <h2 className="text-sm font-semibold text-neutral-900">Funnel by channel</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200 bg-neutral-50/60">
                <th className="text-left px-4 py-2 text-[11px] font-medium text-neutral-500 uppercase tracking-wider">
                  Channel
                </th>
                {STAGE_ORDER.map((s) => (
                  <th
                    key={s}
                    className="text-right px-4 py-2 text-[11px] font-medium text-neutral-500 uppercase tracking-wider"
                  >
                    {STAGE_SHORT[s]}
                  </th>
                ))}
                <th className="text-right px-4 py-2 text-[11px] font-medium text-neutral-500 uppercase tracking-wider">
                  V→Pro
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan={STAGE_ORDER.length + 2}
                    className="text-center px-4 py-8 text-neutral-400 text-xs"
                  >
                    Loading…
                  </td>
                </tr>
              ) : data!.channels.length === 0 ? (
                <tr>
                  <td
                    colSpan={STAGE_ORDER.length + 2}
                    className="text-center px-4 py-8 text-neutral-400 text-xs"
                  >
                    No data in range
                  </td>
                </tr>
              ) : (
                data!.channels.map((ch) => {
                  const visitors = data!.stageByChannel.visitors[ch] ?? 0
                  const pro = data!.stageByChannel.proConverted[ch] ?? 0
                  const conv = visitors > 0 ? (pro / visitors) * 100 : 0
                  return (
                    <tr
                      key={ch}
                      className="border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50/60"
                    >
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <span
                            className="inline-block h-2 w-2 rounded-full"
                            style={{ background: colorForChannel(ch) }}
                          />
                          <span className="font-medium text-neutral-900">
                            {labelForChannel(ch)}
                          </span>
                        </div>
                      </td>
                      {STAGE_ORDER.map((s) => (
                        <td
                          key={s}
                          className="px-4 py-2.5 text-right tabular-nums text-neutral-700"
                        >
                          {formatNumber(data!.stageByChannel[s][ch] ?? 0)}
                        </td>
                      ))}
                      <td className="px-4 py-2.5 text-right tabular-nums">
                        <span
                          className={cn(
                            'font-medium',
                            conv >= 1 ? 'text-emerald-600' : 'text-neutral-400',
                          )}
                        >
                          {formatPct(conv)}
                        </span>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
            {!isLoading && data!.channels.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-neutral-200 bg-neutral-50/60">
                  <td className="px-4 py-2.5 text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                    Total
                  </td>
                  {STAGE_ORDER.map((s) => (
                    <td
                      key={s}
                      className="px-4 py-2.5 text-right tabular-nums font-semibold text-neutral-900"
                    >
                      {formatNumber(data!.stageTotals[s])}
                    </td>
                  ))}
                  <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-neutral-900">
                    {formatPct(
                      data!.stageTotals.visitors > 0
                        ? (data!.stageTotals.proConverted / data!.stageTotals.visitors) * 100
                        : 0,
                    )}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>
    </div>
  )
}
